import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { useOnboardingChatwoot } from '../../integrations/chatwoot/OnboardingChatwootProvider';
import { SvgIcon } from '../icons/SvgIcon';
import { Body } from '../typography/Body';
import { H4 } from '../typography/HeadingLevels';

const SUPPORT_PHONE_DISPLAY = '089 12086364';
const SUPPORT_PHONE_DISPLAY_INTERNATIONAL = '+49 89 12086364';
const SUPPORT_PHONE_TEL = 'tel:+498912086364';

const Card = styled.aside<{ $bottomSpacing: 'default' | 'none'; $variant: 'actions' | 'compact' }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  margin-bottom: ${({ $bottomSpacing }) => ($bottomSpacing === 'none' ? 0 : space.lg)};
  ${({ $variant }) =>
    $variant === 'actions'
      ? `
    padding: 10px;
    border-radius: ${radii.control};
    background: ${getMediumRGBA(0.05)};
  `
      : ''}
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.xxxs};
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const actionRowCss = `
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 32px;
  gap: 5px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: ${radii.sm};
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 120ms ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background: ${getMediumRGBA(0.05)};
    }
  }

  &:active:not(:disabled) {
    background: ${getMediumRGBA(0.09)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ChatRow = styled.button`
  ${actionRowCss}
`;

const PhoneRow = styled.a`
  ${actionRowCss}
`;

const RowIcon = styled.span`
  flex-shrink: 0;
  line-height: 0;
`;

const RowLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.small.fontSize};
  line-height: ${text.small.lineHeight};
  font-weight: 400;
  color: ${colors.medium};
`;

const RowTrailing = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: 10px;
`;

const inlineActionCss = `
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  letter-spacing: inherit;
  color: ${colors.primary};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: ${colors.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ChatLink = styled.button`
  ${inlineActionCss}
`;

const PhoneLink = styled.a`
  ${inlineActionCss}
  display: inline;
`;

export type OnboardingSupportHintProps = {
  /**
   * `actions` — title, subline, and two stacked action rows (Figma `Hilfe Card / Buttons`); used
   *   by the drawers.
   * `compact` — one line of prose with inline chat/phone links (Figma `Hilfe Card / Text`); the
   *   flow's variant, appended by {@link OnboardingBody} on every onboarding screen.
   */
  variant?: 'actions' | 'compact';
  /** `none` drops the card's bottom margin — for containers that own their own rhythm (drawers). */
  bottomSpacing?: 'default' | 'none';
};

/** Support card shown at the bottom of {@link OnboardingBody}, with chat and phone contact options. */
export function OnboardingSupportHint({ variant = 'actions', bottomSpacing = 'default' }: OnboardingSupportHintProps) {
  const { openSupportChat, isOpening } = useOnboardingChatwoot();

  if (variant === 'compact') {
    return (
      <Card aria-label="Support-Kontakt" $bottomSpacing={bottomSpacing} $variant="compact">
        <Body variant="document" tone="muted">
          Fragen? Schreib uns einfach über den{' '}
          <ChatLink
            type="button"
            onClick={() => void openSupportChat()}
            disabled={isOpening}
            aria-busy={isOpening || undefined}
            aria-label="Support-Chat öffnen"
          >
            Chat
          </ChatLink>{' '}
          oder ruf uns unter <PhoneLink href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_DISPLAY_INTERNATIONAL}</PhoneLink> an.
        </Body>
      </Card>
    );
  }

  return (
    <Card aria-label="Support-Kontakt" $bottomSpacing={bottomSpacing} $variant="actions">
      <TextColumn>
        <H4>Fragen zum Kurs oder zur Erstattung?</H4>
        <Body variant="document" tone="muted" size="small">
          Das Physiofit Team hilft dir gerne weiter.
        </Body>
      </TextColumn>

      <RowList>
        <ChatRow
          type="button"
          onClick={() => void openSupportChat()}
          disabled={isOpening}
          aria-busy={isOpening || undefined}
        >
          <RowIcon aria-hidden>
            <SvgIcon name="chatbubble" size={24} primaryColor={colors.medium} />
          </RowIcon>
          <RowLabel>Schreib uns im Chat</RowLabel>
          <RowTrailing aria-hidden>
            <SvgIcon name="arrowright" size={24} primaryColor={colors.medium} />
          </RowTrailing>
        </ChatRow>
        <PhoneRow href={SUPPORT_PHONE_TEL}>
          <RowIcon aria-hidden>
            <SvgIcon name="phone" size={24} primaryColor={colors.medium} />
          </RowIcon>
          <RowLabel>Ruf uns unter {SUPPORT_PHONE_DISPLAY} an</RowLabel>
          <RowTrailing aria-hidden>
            <SvgIcon name="arrowright" size={24} primaryColor={colors.medium} />
          </RowTrailing>
        </PhoneRow>
      </RowList>
    </Card>
  );
}
